import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BellOutlined,
  CameraOutlined,
  FileSearchOutlined,
  FlagOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { WorkspaceId } from '../constants/workspaces';
import { useAuth } from '../contexts/AuthProvider';
import { useWorkspace } from '../contexts/WorkspaceContext';
import ModerationService from '../service/ModerationService';
import {
  fetchPendingBrandRecruiterCount,
  fetchPendingCoachCount,
  fetchPendingScoutCount,
} from '../utils/adminPendingPulse';

interface HeaderNotificationItem {
  id: string;
  title: string;
  detail: string;
  count: number;
  path: string;
  workspaceId: WorkspaceId;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
}

const SEEN_KEY = 'scoutus.admin.notifications.seenAt';

const HeaderNotifications: React.FC = () => {
  const {
    hasPermission,
    isAuthenticated,
    isLoading: authLoading,
    isTokenExpired,
    refreshTokens,
  } = useAuth();
  const { setWorkspaceId } = useWorkspace();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<HeaderNotificationItem[]>([]);
  const [seenAt, setSeenAt] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(SEEN_KEY);
      return raw ? Number(raw) || 0 : 0;
    } catch {
      return 0;
    }
  });
  const [loadedAt, setLoadedAt] = useState(0);

  const canModeration = hasPermission('moderation:read');
  const canAccountApprovals = hasPermission('users:read');
  const canAny = canModeration || canAccountApprovals;

  const canModerationRef = useRef(canModeration);
  const canAccountApprovalsRef = useRef(canAccountApprovals);
  canModerationRef.current = canModeration;
  canAccountApprovalsRef.current = canAccountApprovals;

  const load = useCallback(async () => {
    // Header pulse runs on a 60s timer — refresh first if the bearer is near/past expiry
    // so these four admin GETs don't land as 401s in the API logs.
    if (isTokenExpired()) {
      try {
        await refreshTokens();
      } catch {
        return;
      }
    }

    const next: HeaderNotificationItem[] = [];
    const tasks: Promise<void>[] = [];

    if (canModerationRef.current) {
      tasks.push(
        (async () => {
          try {
            const overview = await new ModerationService().getOverview();
            if (overview.openReports > 0) {
              next.push({
                id: 'reports',
                title: 'Open reports',
                detail: `${overview.openReports.toLocaleString()} waiting for review`,
                count: overview.openReports,
                path: '/v2/moderation/reports',
                workspaceId: 'trust-moderation',
                icon: FlagOutlined,
              });
            }
            if (overview.pendingTextAppeals > 0) {
              next.push({
                id: 'appeals',
                title: 'Appeals',
                detail: `${overview.pendingTextAppeals.toLocaleString()} need a decision`,
                count: overview.pendingTextAppeals,
                path: '/v2/moderation/text-appeals',
                workspaceId: 'trust-moderation',
                icon: FileSearchOutlined,
              });
            }
            if (overview.mediaNeedsReview > 0) {
              next.push({
                id: 'media',
                title: 'AI flagged media',
                detail: `${overview.mediaNeedsReview.toLocaleString()} need review`,
                count: overview.mediaNeedsReview,
                path: '/v2/moderation/media',
                workspaceId: 'trust-moderation',
                icon: CameraOutlined,
              });
            }
          } catch {
            // ignore — notifications are best-effort
          }
        })(),
      );
    }

    if (canAccountApprovalsRef.current) {
      tasks.push(
        (async () => {
          try {
            const [coaches, brand, scouts] = await Promise.all([
              fetchPendingCoachCount(),
              fetchPendingBrandRecruiterCount(),
              fetchPendingScoutCount(),
            ]);
            const count = coaches + brand + scouts;
            if (count > 0) {
              next.push({
                id: 'account-approvals',
                title: 'Account approvals',
                detail: `${count.toLocaleString()} pending`,
                count,
                path: '/v2/trust',
                workspaceId: 'trust-moderation',
                icon: TeamOutlined,
              });
            }
          } catch {
            // ignore
          }
        })(),
      );
    }

    await Promise.all(tasks);
    next.sort((a, b) => b.count - a.count);
    setItems(next);
    setLoadedAt(Date.now());
  }, [isTokenExpired, refreshTokens]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAny) return;

    let cancelled = false;
    let intervalId: number | undefined;
    let idleId: number | undefined;
    let timeoutId: number | undefined;

    const start = () => {
      if (cancelled) return;
      void load();
      intervalId = window.setInterval(() => {
        void load();
      }, 60_000);
    };

    // Defer so page-critical analytics XHRs get the browser connection pool first.
    // DEV analytics bundles often take 30–60s; don't compete for slots early.
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(start, { timeout: 25_000 });
    } else {
      timeoutId = window.setTimeout(start, 20_000);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [authLoading, isAuthenticated, canAny, load]);

  const totalCount = useMemo(
    () => items.reduce((sum, item) => sum + item.count, 0),
    [items],
  );

  const hasUnread = totalCount > 0 && loadedAt > seenAt;

  const markSeen = () => {
    const now = Date.now();
    setSeenAt(now);
    try {
      localStorage.setItem(SEEN_KEY, String(now));
    } catch {
      // ignore
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          const nextOpen = !open;
          setOpen(nextOpen);
          if (nextOpen) markSeen();
        }}
        className="relative flex h-9 w-9 items-center justify-center rounded-xl text-foreground-muted transition-colors hover:bg-background-tertiary hover:text-foreground"
        aria-label={hasUnread ? `Notifications, ${totalCount} items` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <BellOutlined style={{ fontSize: 16 }} />
        {hasUnread && (
          <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-accent-primary ring-2 ring-background-secondary" />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-[320px] overflow-hidden rounded-2xl border border-border bg-background-secondary shadow-xl shadow-black/10">
            <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
              <p className="text-sm font-semibold text-foreground">Notifications</p>
              {totalCount > 0 && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:bg-slate-700 dark:text-[#D9F42B]">
                  {totalCount}
                </span>
              )}
            </div>

            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-foreground-muted">
                You&apos;re all caught up
              </p>
            ) : (
              <ul className="max-h-80 overflow-y-auto p-1.5">
                {items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setOpen(false);
                          setWorkspaceId(item.workspaceId);
                          navigate(item.path);
                        }}
                        className="flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-background"
                      >
                        <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-accent-primary dark:bg-slate-700 dark:text-[#D9F42B]">
                          <Icon style={{ fontSize: 14 }} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-[13px] font-semibold text-foreground">
                              {item.title}
                            </span>
                            <span className="rounded-md bg-background px-1.5 py-0.5 text-[10px] font-semibold text-foreground-muted ring-1 ring-border">
                              {item.count}
                            </span>
                          </span>
                          <span className="mt-0.5 block truncate text-[11px] text-foreground-muted">
                            {item.detail}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HeaderNotifications;
